import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICliente,
  ICreateLuminaria,
  IQueryParam,
  IUpdateLuminaria,
  IFilter,
  ILuminaria,
  IDispositivoLorawan,
  IGeoJSONPoint,
  IConfigPerfil,
  TipoEntidadConfigPerfil,
  IGrupo,
  IPuesta,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { LoginService } from '../../../../login/login.service';
import { LuminariasHttpService } from '../../../../../auxiliares/servicios/http/luminarias.service';
import { ActivatedRoute } from '@angular/router';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { DireccionesService } from '../../../../../auxiliares/servicios/http/direcciones.service';
import { PuestasService } from '../../../../../auxiliares/servicios/http/puestas.service';
import { ClientesService } from '../../../../../auxiliares/servicios/http/clientes.service';
import { CrearEditarPuestaComponent } from '../../../../standalone/puestas/crear-editar-puesta/crear-editar-puesta.component';
import { CrearEditarConfigPerfilComponent } from '../../../../standalone/config-perfil/crear-editar-config-perfil/crear-editar-config-perfil.component';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-crear-editar-luminaria',
  templateUrl: './crear-editar-luminaria.component.html',
  styleUrl: './crear-editar-luminaria.component.scss',
  standalone: false,
})
export class CrearEditarLuminariaComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private service = inject(LuminariasHttpService);
  paramsService = inject(ParamsService);
  dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private route = inject(ActivatedRoute);
  private geocodingService = inject(DireccionesService);
  private puestasService = inject(PuestasService);
  private clientesService = inject(ClientesService);
  data = inject<{
    edit?: ILuminaria;
  }>(MAT_DIALOG_DATA, { optional: true });
  dialogRef? = inject<MatDialogRef<CrearEditarLuminariaComponent>>(
    MatDialogRef,
    { optional: true },
  );

  public id?: string | null;
  public clientePropio = LoginService.getCliente();
  public loading = false;
  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;

  ////////////////////////////////////////////////////////////////////////////
  public dispositivosLorawan: IDispositivoLorawan[] = [];
  public dispositivosLorawan$?: Subscription;
  public luminaria?: ILuminaria;
  public luminaria$?: Subscription;

  public perfilesConfig?: IConfigPerfil[] = [];
  public perfilesConfigFiltrados?: IConfigPerfil[] = [];
  public perfilesConfig$?: Subscription;
  public perfilConfigSeleccionado?: IConfigPerfil;

  public gruposSeleccionados: IGrupo[] = [];

  // Puestas (clientes con moduloLuminarias.usaPuestas): la luminaria toma su
  // ubicación de la puesta seleccionada.
  public puestas: IPuesta[] = [];

  // Cliente DE LA LUMINARIA (no el logueado). Decide si se usan puestas: un
  // mayorista/minorista con usaPuestas puede estar editando una luminaria de un
  // sub-cliente SIN usaPuestas → en ese caso no se muestran ni se envían puestas.
  public clienteLuminaria?: ICliente;

  public get usaPuestas(): boolean {
    const cliente = this.clienteLuminaria ?? this.clientePropio;
    return !!cliente?.config?.moduloLuminarias?.usaPuestas;
  }

  public centrarA?: IGeoJSONPoint;

  public clienteElegido?: ICliente;
  public tipoCliente = HelperService.getTipoCliente();

  // Para manejar conflictos de deveui
  public luminariaConflicto?: ILuminaria;
  public verificandoDeveui = false;
  public deveuiVerificado = false;
  ////////////////////////////////////////////////////////////////////////
  get geojson() {
    return this.form?.get('ubicacion') as FormGroup;
  }

  // Ya no hay conflictos de perfil propio vs grupos ni entre grupos: la
  // jerarquía estricta (luminaria > grupo por prioridad > puesta > grupoPuesta)
  // resuelve siempre un único perfil efectivo.
  get mostrarCamposCliente(): boolean {
    return this.tipoCliente !== 'Final';
  }
  ///////////////////////////////////////////////////////////////////////
  //FORM

  private createForm() {
    // Determinar el cliente por defecto basado en el tipo de usuario
    const clienteDefault =
      this.tipoCliente === 'Final' && this.clientePropio
        ? this.clientePropio._id
        : this.luminaria?.idCliente;

    this.form = new FormGroup({
      identificacion: new FormControl(
        this.luminaria?.identificacion,
        Validators.required,
      ),
      idCliente: new FormControl(clienteDefault, Validators.required),
      deveui: new FormControl(this.luminaria?.deveui),
      idsGrupos: new FormControl(this.luminaria?.idsGrupos),
      idModeloDispositivo: new FormControl(this.luminaria?.idModeloDispositivo),
      direccion: new FormControl(this.luminaria?.direccion),
      ubicacion: new FormGroup({
        type: new FormControl('Point'),
        coordinates: new FormControl(this.luminaria?.ubicacion?.coordinates),
      }),
      idPerfilConfig: new FormControl(this.luminaria?.idPerfilConfig),
      idPuesta: new FormControl(this.luminaria?.idPuesta),
      puedeSolicitarServicioTecnico: new FormControl(
        this.luminaria?.puedeSolicitarServicioTecnico ?? false,
      ),
    });

    // Listener para verificar deveui existente cuando cambie
    this.form
      .get('deveui')
      ?.valueChanges.pipe(
        debounceTime(500), // Esperar 500ms después del último cambio
        distinctUntilChanged(), // Solo proceder si el valor realmente cambió
      )
      .subscribe(async (deveui: string) => {
        // Resetear estados cuando cambia el deveui
        this.deveuiVerificado = false;
        this.luminariaConflicto = undefined;

        if (deveui && deveui.trim()) {
          await this.verificarDeveuiExistente(deveui.trim());
          // Filtrar perfiles según tipo de dispositivo
          await this.filtrarPerfilesPorDispositivo(deveui.trim());
        } else {
          this.perfilesConfigFiltrados = this.perfilesConfig;
        }
      });
  }

  ///////////////////////////////////////////////////////////////////////////
  //PUESTAS (solo clientes con usaPuestas)

  // Carga el cliente DE LA LUMINARIA (el del form) para resolver su `usaPuestas`.
  // Si coincide con el cliente logueado, reusa ese (evita un fetch).
  private async cargarClienteLuminaria(): Promise<void> {
    const idCliente = this.form?.get('idCliente')?.value;
    if (!idCliente) {
      this.clienteLuminaria = undefined;
      return;
    }
    if (idCliente === this.clientePropio?._id) {
      this.clienteLuminaria = this.clientePropio;
      return;
    }
    try {
      this.clienteLuminaria = await this.clientesService.getById(idCliente);
    } catch (error) {
      console.error('Error cargando cliente de la luminaria:', error);
      this.clienteLuminaria = undefined;
    }
  }

  public async listarPuestas(): Promise<void> {
    if (!this.usaPuestas) return;
    try {
      // Solo puestas del cliente de la luminaria (el filtro `{ idCliente }` ya
      // lo restringe). includeChildren: true habilita la autorización de tenancy
      // del backend para que un usuario de un cliente ANCESTRO pueda ver las
      // puestas del cliente (descendiente) de la luminaria.
      const idCliente = this.form?.get('idCliente')?.value;
      const filter: IFilter<IPuesta> = idCliente ? { idCliente } : {};
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'identificacion ubicacion',
        sort: 'identificacion',
        includeChildren: true,
        limit: 0,
      };
      const data = await this.puestasService.getFiltered(query);
      this.puestas = data.datos ?? [];
    } catch (error) {
      console.error('Error al listar puestas:', error);
      this.puestas = [];
    }
  }

  // Al elegir una puesta, la luminaria adopta su ubicación (la puesta es la
  // fuente de verdad de la georreferencia). El backend la re-denormaliza igual.
  public onPuestaChange(idPuesta?: string): void {
    const puesta = this.puestas.find((p) => p._id === idPuesta);
    const coords = puesta?.ubicacion?.coordinates;
    if (coords) {
      this.form
        ?.get('ubicacion')
        ?.patchValue({ type: 'Point', coordinates: coords });
      this.centrarA = puesta?.ubicacion;
    }
  }

  // Crear una puesta nueva inline desde el alta/edición de la luminaria y
  // seleccionarla automáticamente.
  public async crearPuesta(): Promise<void> {
    const ref = this.formularios.abrir(CrearEditarPuestaComponent, {
      tamano: 'mediano',
    });
    const creada: IPuesta | undefined = await firstValueFrom(ref.afterClosed());
    if (creada?._id) {
      await this.listarPuestas();
      this.form?.patchValue({ idPuesta: creada._id });
      this.onPuestaChange(creada._id);
    }
  }

  ///////////////////////////////////////////////////////////////////////////
  //LISTAR

  public async listar(): Promise<void> {
    this.luminaria$?.unsubscribe();
    this.luminaria = await firstValueFrom(this.lista2.luminaria(this.id!, 30));
  }

  public async listarPerfilesConfig(): Promise<void> {
    try {
      const tipoEntidad: TipoEntidadConfigPerfil = 'Luminaria';
      // Solo perfiles del cliente de la luminaria + los globales (usables por
      // cualquier cliente). Antes traía perfiles de todo el árbol accesible.
      const idCliente = this.form?.get('idCliente')?.value;
      const filter: IFilter<IConfigPerfil> = idCliente
        ? { tipoEntidad, $or: [{ idCliente }, { global: true }] }
        : { tipoEntidad };
      // includeChildren: true SIEMPRE. El `$or` de arriba ya restringe a
      // "cliente exacto de la luminaria + globales"; includeChildren no amplía
      // ese filtro, solo habilita la AUTORIZACIÓN de tenancy del backend
      // (rama {idsAncestros: permisoCliente}). Sin él, un usuario de un cliente
      // ANCESTRO no puede ver el perfil del cliente (descendiente) de la luminaria.
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        includeChildren: true,
        sort: '-fechaCreacion',
        select: 'nombre global idCliente _id tipoConfig',
      };
      this.perfilesConfig$?.unsubscribe();
      this.perfilesConfig$ = this.lista2
        .configPerfils(query, 30)
        .subscribe((data) => {
          this.perfilesConfig = data.datos.filter((pc) => pc && pc._id);
          this.filtrarPerfilesPorDispositivo(this.form?.get('deveui')?.value);
        });
    } catch (error) {
      console.error('Error al listar perfiles config de luminaria:', error);
      this.perfilesConfig = [];
    }
  }

  public async filtrarPerfilesPorDispositivo(deveui: string): Promise<void> {
    if (!deveui || !this.perfilesConfig) {
      this.perfilesConfigFiltrados = this.perfilesConfig;
      return;
    }

    try {
      const dispositivoIndex = this.dispositivosLorawan.findIndex(
        (d) => d.deveui === deveui,
      );
      if (dispositivoIndex === -1) {
        this.perfilesConfigFiltrados = this.perfilesConfig;
        return;
      }

      const dispositivo = this.dispositivosLorawan[dispositivoIndex];
      const tipoDispositivo = dispositivo.tipo || dispositivo.name; // Obtener tipo del dispositivo

      // Para luminarias ACTIS FING sólo se asigna el perfil "General"; el
      // perfil "Dimming" se referencia desde el General y no se asigna
      // directamente a la luminaria.
      const tipoPerfilEsperado =
        tipoDispositivo === 'Luminaria ACTIS FING'
          ? 'Luminaria ACTIS FING General'
          : tipoDispositivo;

      this.perfilesConfigFiltrados = this.perfilesConfig.filter(
        (perfil) => perfil.tipoConfig === tipoPerfilEsperado,
      );
    } catch (error) {
      console.error('Error al filtrar perfiles por dispositivo:', error);
      this.perfilesConfigFiltrados = this.perfilesConfig;
    }
  }

  public async listarDispositivoLorawan() {
    try {
      const filter: IFilter<IDispositivoLorawan> = {};
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: 'name',
        limit: 0,
        includeChildren: true,
      };
      this.dispositivosLorawan$?.unsubscribe();
      this.dispositivosLorawan$ = this.lista2
        .dispositivosLorawans(query, 30)
        .subscribe((data) => {
          if (data && data.datos && Array.isArray(data.datos)) {
            this.dispositivosLorawan = data.datos.filter(
              (d) => d && d.deveui && d.name,
            );
          } else {
            this.dispositivosLorawan = [];
          }
          // Re-filtrar perfiles por tipo de dispositivo: en edición el deveui
          // ya está seteado y no dispara valueChanges, y esta lista puede
          // llegar después que los perfiles (carrera entre suscripciones).
          this.filtrarPerfilesPorDispositivo(this.form?.get('deveui')?.value);
        });
    } catch (error) {
      console.error('Error al listar dispositivos LoRaWAN:', error);
      this.dispositivosLorawan = [];
    }
  }

  public async verificarDeveuiExistente(deveui: string): Promise<void> {
    if (!deveui) {
      this.luminariaConflicto = undefined;
      this.deveuiVerificado = false;
      return;
    }

    this.verificandoDeveui = true;
    this.deveuiVerificado = false;
    try {
      const filter: IFilter<ILuminaria> = {
        deveui: deveui,
      };
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'identificacion deveui idCliente',
        limit: 1,
        includeChildren: true,
      };

      const resultado = await firstValueFrom(this.lista2.luminarias(query, 0));

      if (resultado && resultado.datos && resultado.datos.length > 0) {
        const luminariaExistente = resultado.datos[0];

        // Solo mostrar conflicto si no es la misma luminaria que estamos editando
        if (this.id && luminariaExistente._id === this.id) {
          // Es la misma luminaria que estamos editando, no hay conflicto
          this.luminariaConflicto = undefined;
        } else {
          // Es una luminaria diferente, hay conflicto
          this.luminariaConflicto = luminariaExistente;
        }
      } else {
        this.luminariaConflicto = undefined;
      }
    } catch (error) {
      console.error('Error al verificar deveui existente:', error);
      this.luminariaConflicto = undefined;
    } finally {
      this.verificandoDeveui = false;
      this.deveuiVerificado = true;
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////
  //FUNCIONES

  //GUARDAR
  public async onSubmit() {
    this.loading = true;
    try {
      if (this.id) {
        const data = this.getData();
        if (data) {
          // Update
          await this.service.update(this.id, data);
          const luminariaActualizada = await this.service.getById(this.id);
          this.helper.notifSuccess('Luminaria actualizada correctamente');
          this.close(luminariaActualizada);
        }
      } else {
        // Create
        const data = this.getData();
        if (data) {
          await this.service.create(data);
          this.helper.notifSuccess('Luminaria creada correctamente');
          this.close();
        }
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  //CAMBIO DE CLIENTE
  public async onClienteChange() {
    this.form?.patchValue({ idsGrupos: null });
    // Los perfiles dependen del cliente: limpiar selección y recargar filtrando
    // por el nuevo cliente.
    this.form?.patchValue({ idPerfilConfig: null });
    await this.listarPerfilesConfig();
    // Recalcular usaPuestas según el cliente elegido. Si el nuevo cliente no
    // usa puestas, limpiar la selección y la lista; si usa, recargar puestas.
    await this.cargarClienteLuminaria();
    if (!this.usaPuestas) {
      this.form?.patchValue({ idPuesta: null });
      this.puestas = [];
    } else {
      await this.listarPuestas();
    }
  }

  //OBTENCIÓN DE DATOS A PARTIR DEL FORM
  public getData(): ICreateLuminaria | IUpdateLuminaria | null {
    if (!this.form || !this.form.valid) {
      return null;
    }
    const data: ICreateLuminaria | IUpdateLuminaria = this.form.getRawValue();
    // Si el cliente de la luminaria no usa puestas, no enviar idPuesta: el
    // backend rechaza idPuesta para clientes sin usaPuestas (y la sección no se
    // muestra). Evita el 400 al editar luminarias de sub-clientes sin puestas.
    if (!this.usaPuestas) {
      delete (data as { idPuesta?: unknown }).idPuesta;
    }
    // Sin coordenadas (nadie ubicó el marcador), no mandar `ubicacion`: un
    // `{ type: 'Point' }` sin coordenadas no es un GeoJSON válido.
    if (data.ubicacion?.coordinates?.length !== 2) {
      delete (data as { ubicacion?: unknown }).ubicacion;
    }
    // Clientes que atienden ST: En luminarias el usuario NO tiene ingerencia. Siempre se envía
    // solo el cliente propio. configHorariosAtencionTecnica existe únicamente por coherencia con otros dispositivos del sistmea.
    const idClientePropio = this.clientePropio?._id;
    if (idClientePropio) {
      (data as ILuminaria).idsClientesQuePuedenAtenderEventosTecnicos = [
        idClientePropio,
      ];
      (data as ILuminaria).configHorariosAtencionTecnica = [
        { idCliente: idClientePropio },
      ];
    }
    return data;
  }

  public close(luminariaActualizada?: ILuminaria): void {
    if (this.dialogRef) {
      //Si es un diálogo, cerramos sólo es el dialog
      this.dialogRef.close(luminariaActualizada);
    } else {
      this.helper.volver(); //Si no es un dialog, volvemos a la página anterior
    }
  }

  //PARCHEAR DIRECCIÓN DESDE MAPA
  // Modificar el método patchDireccion
  public patchDireccion(direccion: string) {
    this.form?.patchValue({ direccion: direccion });
  }

  //GEOCODING REVERSO (ubicacion -> dirección)
  public async reverseGeocodeFromMap() {
    const ubicacion = this.form?.get('ubicacion')?.value;
    if (ubicacion?.coordinates?.length !== 2) {
      this.helper.notifError('Ubicación no válida para buscar dirección.');
      return;
    }
    try {
      // Luminaria → precisión 9. (Se pasa `ubicacion.coordinates`, no el objeto.)
      const resultado = await this.geocodingService.reverse(
        ubicacion.coordinates,
        9,
      );

      if (resultado.direccion) {
        this.luminaria!.direccion = resultado.direccion; //Esto es para que se actualice visualmente rápido en el componente
        this.form?.patchValue({ direccion: resultado.direccion }); //Acá se guarda en el form y esto es lo que se guarda realmente en la luminaria
        this.helper.notifSuccess('Dirección actualizada desde el mapa.');
      } else {
        this.helper.notifError(
          'No se pudo encontrar una dirección para esta ubicación.',
        );
      }
    } catch (e) {
      this.helper.notifError('Error al buscar la dirección.');
      console.error(e);
    }
  }

  public onGruposChange(grupos: IGrupo[]): void {
    this.gruposSeleccionados = grupos;
  }

  private async listarGruposIniciales(): Promise<void> {
    const idsGrupos = this.luminaria?.idsGrupos;
    if (!idsGrupos?.length) return;
    const filter: IFilter<IGrupo> = {
      _id: { $in: idsGrupos },
    } as IFilter<IGrupo>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: '_id nombre idsPerfilConfig',
      limit: 0,
    };
    const result = await firstValueFrom(this.lista2.grupos(query, 0));
    this.gruposSeleccionados = result?.datos ?? [];
  }

  // Tipo del dispositivo Lorawan actualmente seleccionado (por deveui).
  public get tipoDispositivoSeleccionado(): string | undefined {
    const deveui = this.form?.get('deveui')?.value;
    if (!deveui) return undefined;
    const disp = this.dispositivosLorawan.find((d) => d.deveui === deveui);
    return disp?.tipo || disp?.name;
  }

  // Crear un perfil inline. El tipo se fuerza según el dispositivo de la
  // luminaria: ACTIS FING → sólo perfil "General" (configuración); el resto, el
  // tipo del propio dispositivo. Tras crear, se selecciona automáticamente.
  public async crearPerfil(): Promise<void> {
    const tipoDispositivo = this.tipoDispositivoSeleccionado;
    if (!tipoDispositivo) {
      this.helper.notifError('Seleccioná un dispositivo Lorawan primero');
      return;
    }
    const tipoConfigForzado =
      tipoDispositivo === 'Luminaria ACTIS FING'
        ? 'Luminaria ACTIS FING General'
        : tipoDispositivo;
    const ref = this.formularios.abrir(CrearEditarConfigPerfilComponent, {
      tamano: 'mediano',
      data: { tipoEntidad: 'Luminaria', tipoConfigForzado },
    });
    const creado: IConfigPerfil | undefined = await firstValueFrom(
      ref.afterClosed(),
    );
    if (creado?._id) {
      await this.listarPerfilesConfig();
      this.form?.patchValue({ idPerfilConfig: creado._id });
    }
  }

  //SELECTOR DE PERFILES
  seleccionarConfig(id: string | null) {
    this.form.patchValue({ idPerfilConfig: id });
  }

  esConfigSeleccionada(id: string) {
    return this.form.value.idPerfilConfig === id;
  }

  private setTitles() {
    if (this.id) {
      this.title = 'Editar Luminaria';
      this.subtitle = this.luminaria?.identificacion;
    } else {
      this.title = 'Crear Luminaria';
    }
  }
  /////////////////////////////////////////////////////////////////////////////
  //HOOKS
  async ngOnInit() {
    this.loading = true;

    try {
      const params = await firstValueFrom(this.route.paramMap);
      this.id = params.get('id') || this.data?.edit?._id;

      if (this.id) {
        await this.listar();
        await this.listarGruposIniciales();
      }

      // Crear formulario después de cargar los datos
      this.createForm();
      this.setTitles();

      // Resolver el cliente de la luminaria ANTES de listar puestas
      // (listarPuestas depende de usaPuestas, que mira ese cliente).
      await this.cargarClienteLuminaria();

      // Cargar datos en paralelo
      await Promise.all([
        this.listarDispositivoLorawan(),
        this.listarPerfilesConfig(),
        this.listarPuestas(),
      ]);

      // Si es usuario final, establecer automáticamente el cliente
      if (this.tipoCliente === 'Final') {
        // Asegurar que los valores estén establecidos en el formulario
        this.form?.patchValue({
          idCliente: this.clientePropio._id,
        });
      }
    } catch (error) {
      console.error('Error en ngOnInit:', error);
      this.helper.notifError('Error al cargar los datos');
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    // Cancelar todas las suscripciones
    this.dispositivosLorawan$?.unsubscribe();
    this.luminaria$?.unsubscribe();
    this.perfilesConfig$?.unsubscribe();
  }
}
